import {
  CHECKOUT_V2_DISMISS_CART_CHANGES,
  CHECKOUT_V2_CLEAR_PAYMENT_ATTEMPT,
  CHECKOUT_V2_CLEAR_STEP,
  CHECKOUT_V2_CLOSE_AUTH,
  CHECKOUT_V2_OPEN_AUTH,
  CHECKOUT_V2_SET_STEP,
  CHECKOUT_V2_START_PAYMENT_ATTEMPT,
  CHECKOUT_V2_UPDATE_PAYMENT_ATTEMPT,
  CHECKOUT_V2_SET_CONTEXT,
  CHECKOUT_V2_PATCH_DRAFT,
  CHECKOUT_V2_SET_REMOVED_LINES,
  CHECKOUT_V2_SET_QUOTE,
  CHECKOUT_V2_CLEAR_QUOTE,
  CHECKOUT_V2_SET_CART_VALIDATION,
  CHECKOUT_V2_CLEAR_CART_VALIDATION,
} from "./reducer";
import {
  createFulfillmentFingerprint,
  hasFulfillmentLocationChanged,
  invalidateAddressForLocationChange,
  normalizeFulfillmentContext,
} from "../domain/fulfillmentContext";

export const dismissCartChanges = (keys) => ({ type: CHECKOUT_V2_DISMISS_CART_CHANGES, payload: keys });

export const setCheckoutV2Step = (step) => ({
  type: CHECKOUT_V2_SET_STEP,
  payload: step,
});

export const clearCheckoutV2Step = () => ({ type: CHECKOUT_V2_CLEAR_STEP });

export const openCheckoutV2Auth = (options = {}) => ({
  type: CHECKOUT_V2_OPEN_AUTH,
  payload: options,
});

export const closeCheckoutV2Auth = () => ({ type: CHECKOUT_V2_CLOSE_AUTH });

export const startCheckoutV2PaymentAttempt = (attempt) => ({
  type: CHECKOUT_V2_START_PAYMENT_ATTEMPT,
  payload: attempt,
});

export const updateCheckoutV2PaymentAttempt = (changes = {}) => ({
  type: CHECKOUT_V2_UPDATE_PAYMENT_ATTEMPT,
  payload: { ...changes, updatedAt: changes.updatedAt || Date.now() },
});

export const clearCheckoutV2PaymentAttempt = () => ({
  type: CHECKOUT_V2_CLEAR_PAYMENT_ATTEMPT,
});

export const setCheckoutV2Context = (context) => ({
  type: CHECKOUT_V2_SET_CONTEXT,
  payload: context,
});

export const patchCheckoutV2Draft = (draft) => ({
  type: CHECKOUT_V2_PATCH_DRAFT,
  payload: draft,
});

export const setCheckoutV2RemovedLines = (lineIds) => ({
  type: CHECKOUT_V2_SET_REMOVED_LINES,
  payload: lineIds,
});

export const setCheckoutV2Quote = (quote) => ({
  type: CHECKOUT_V2_SET_QUOTE,
  payload: quote,
});

export const clearCheckoutV2Quote = () => ({ type: CHECKOUT_V2_CLEAR_QUOTE });

export const setCheckoutV2CartValidation = (validation) => ({
  type: CHECKOUT_V2_SET_CART_VALIDATION,
  payload: validation,
});

export const clearCheckoutV2CartValidation = () => ({
  type: CHECKOUT_V2_CLEAR_CART_VALIDATION,
});

export const setCheckoutV2PickupMode = (pickupMode) => (
  dispatch,
  getState,
) => {
  const normalizedMode = ["inside", "car"].includes(pickupMode)
    ? pickupMode
    : null;
  dispatch({
    type: "MODIFY_ADDRESS",
    payload: {
      pickupMode: normalizedMode,
      carPickup: normalizedMode === "car",
    },
  });
  const state = getState();
  const context = normalizeFulfillmentContext({
    address: state.customers?.address,
    branch: state.branch,
    deliveryTime: state.deliveryTime,
    settings: state.store?.store?.settings,
  });
  dispatch(
    setCheckoutV2Context({
      ...context,
      fingerprint: createFulfillmentFingerprint(context),
    }),
  );
};

const INVALIDATED_DELIVERY_ADDRESS = Object.freeze({
  id: null,
  name: "",
  orderPassAddressId: null,
  checkoutAddress: null,
  selectedAddressType: 0,
  block: "",
  zone: "",
  street: "",
  avenue: "",
  building: "",
  floor: "",
  apartment: "",
  additional: "",
  paci: "",
  location_id: "",
  home_picture: null,
  lat: "",
  lng: "",
  gpsConfirmed: false,
  gps_confirmed: false,
});

const getCapturedDevicePosition = (context = {}) => {
  const address = context.address || {};
  const lat = Number(address.lat);
  const lng = Number(address.lng);
  if (
    context.type !== "delivery" ||
    address.capturedFromDevice !== true ||
    address.gpsConfirmed !== true ||
    !Number.isFinite(lat) ||
    !Number.isFinite(lng) ||
    lat < -90 ||
    lat > 90 ||
    lng < -180 ||
    lng > 180
  )
    return null;
  return { lat, lng };
};

export const commitCheckoutV2Context = (context) => (dispatch, getState) => {
  const state = getState();
  const currentContext = normalizeFulfillmentContext({
    address: state.customers?.address,
    branch: state.branch,
    deliveryTime: state.deliveryTime,
    settings: state.store?.store?.settings,
  });
  const locationChanged = hasFulfillmentLocationChanged(
    currentContext,
    context,
  );
  const invalidatedContext = invalidateAddressForLocationChange(
    currentContext,
    context,
  );
  const capturedPosition = getCapturedDevicePosition(context);
  const nextContext = capturedPosition
    ? {
        ...invalidatedContext,
        address: {
          ...(invalidatedContext.address || {}),
          ...capturedPosition,
          gpsConfirmed: true,
        },
      }
    : invalidatedContext;
  const pickup = nextContext?.type === "pickup";
  const delivery = nextContext?.type === "delivery";
  dispatch({
    type: "MODIFY_ADDRESS",
    payload: {
      ...(locationChanged ? INVALIDATED_DELIVERY_ADDRESS : {}),
      selectedDeliveryType: delivery ? 0 : pickup ? 1 : undefined,
      country: delivery ? nextContext.countryId : null,
      area: delivery ? nextContext.areaId : null,
      pickupArea: pickup ? nextContext.branchId : null,
      pickupMode: pickup ? nextContext.pickupMode : null,
      carPickup: pickup && nextContext.pickupMode === "car",
      make: pickup ? nextContext.vehicle?.make || "" : "",
      color: pickup ? nextContext.vehicle?.color || "" : "",
      plateNumber: pickup ? nextContext.vehicle?.plateNumber || "" : "",
      ebutlerRow: pickup ? nextContext.vehicle?.parkingSpot || "" : "",
      lat: delivery ? nextContext.address?.lat ?? "" : "",
      lng: delivery ? nextContext.address?.lng ?? "" : "",
      gpsConfirmed:
        delivery && nextContext.address?.gpsConfirmed === true,
      gps_confirmed:
        delivery && nextContext.address?.gpsConfirmed === true,
    },
  });
  dispatch({
    type: "SET_BRANCH_DELIVERY_CHARGE",
    payload: {
      branch: nextContext?.branchId || null,
      bdc: delivery ? nextContext?.branchDeliveryChargeId || null : null,
      isPickup: pickup,
    },
  });
  if (nextContext?.time) {
    dispatch({
      type: "CHECKOUT_V2_COMMIT_DELIVERY_TIME",
      payload: {
        type: nextContext.time.type,
        expected_date: nextContext.time.date,
        expected_start: nextContext.time.start,
        expected_end: nextContext.time.end,
        within: nextContext.time.withinMinutes ?? nextContext.time.within ?? null,
        authority: nextContext.time.authority || "",
        isPickup: pickup,
      },
    });
  }
  const committedContext = {
    ...nextContext,
    fingerprint: createFulfillmentFingerprint(nextContext),
  };
  dispatch(
    setCheckoutV2Context(committedContext),
  );
  dispatch(clearCheckoutV2Quote());
};
